import styled, { css, StyledProps } from 'styled-components'
import { ReactComponent as LogoImage } from 'assets/images/logo.svg'
import IconButtonComponent from 'views/ui/IconButton/IconButton'
import { IconBurgerMenu, IconCross, Flag, Button } from '@agro-club/frontend-shared'
import Link from 'views/components/Link/Link'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { components as selectComponents } from 'react-select'

type ThemedProps = StyledProps<{}>

export const Wrapper = styled.div`
  display: flex;
  align-items: center;
  max-width: ${(props: ThemedProps) => props.theme.size.maxContentWidth}px;
  margin: 0 auto;
  padding: 0;
  height: 100%;

  ${props => props.theme.media.mobile`
    padding: 0 8px 0 11px;
  `}
`
export const ButtonsWrapper = styled.div`
  margin-left: auto;
  flex-shrink: 0;
  justify-content: center;
  display: grid;
  align-items: center;
  grid-gap: 8px;
  grid-template-columns: max-content max-content max-content;
`

export const IconButton = styled(IconButtonComponent)`
  ${props => props.theme.media.mobile`
    height: 32px;
    min-width: 32px;
    padding: 6px;
  `}
`

export const CountBadge = styled.span`
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  background: ${(props: ThemedProps) => props.theme.color.accentNotify};
  position: absolute;
  right: -5px;
  top: -5px;
  font-weight: 600;
  font-size: 12px;
  text-align: center;
  line-height: 20px;
  color: ${(props: ThemedProps) => props.theme.color.onPrimaryLight};
  flex-shrink: 0;

  ${props => props.theme.media.mobile`
    position: relative;
    top: auto;
    right: auto;
    margin-left: 6px;
    display: block;
  `}
`

export const LangWrapper = styled.div`
  display: flex;
  margin-left: 8px;

  ${props => props.theme.media.mobile`
    margin-top: 24px;
    margin-left: 32px;
  `}
`

export const Lang = styled.button`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  margin-left: 16px;
  background: none;
  border: none;
  border-radius: 4px;

  &:focus {
    outline: none;
    box-shadow: ${(props: StyledProps<{ active: boolean }>) => props.theme.shadow.focus};
  }

  ${(props: StyledProps<{ active: boolean }>) => css`
    color: ${props.active ? props.theme.color.onPrimaryDark : props.theme.color.secondary200};
    pointer-events: ${props.active ? 'none' : 'initial'};
  `};

  ${props => props.theme.media.mobile`
    font-size: 24px;
    margin-left: 0;
    margin-right: 16px;

    ${(props: StyledProps<{ active: boolean }>) => css`
      opacity: ${props.active ? 1 : 0.24};
      color: ${props.theme.color.onPrimaryDark};
    `}
  `}
`

export const NavItem = styled(Link)`
  margin-left: 40px;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${(props: StyledProps<{}>) => props.theme.color.onPrimaryDark};
  display: flex;
  align-items: center;

  ${props => props.theme.media.smallTablet`
    margin-left: 20px;
  `}

  ${props => props.theme.media.mobile`
    margin-top: 24px;
    font-size: 24px;
    position: relative;
  `}
`
export const CanadaFlag = styled(Flag)`
  margin-right: 4px;
  font-size: 20px;
  line-height: 20px;

  ${props => props.theme.media.mobile`
    font-size: 24px;
    position: absolute;
    transform: translateX(calc(-100% - 2px));
  `}
`

export const AgroClubLogo = styled(LogoImage)`
  display: flex;
  align-items: center;
  ${props => props.theme.media.smallTablet`
    height: 60px;
  `}
  ${props => props.theme.media.mobile`
    height: 48px;
  `}
`

export const WhiteLabelLogo = styled.img`
  display: flex;
  align-items: center;
  height: 54px;
  margin-bottom: 8px;
  ${props => props.theme.media.smallTablet`
    height: 54px;
  `}
  ${props => props.theme.media.mobile`
    height: 48px;
    margin-left: 24px;
  `}
`

export const ButtonText = styled.span`
  ${props => props.theme.media.mobile`
    display: none;
  `}
`

export const MobileNav = styled.div`
  order: -1;
  position: relative;
  margin-right: -15px;
`

export const NavItems = styled.div`
  flex-direction: column;
  position: fixed;
  width: 100%;
  height: 100vh;
  z-index: 10;
  top: 0;
  left: 0;
  padding-top: 64px;

  ${(props: StyledProps<{ isOpen: boolean }>) => css`
    display: ${props.isOpen ? 'flex' : 'none'};
    background-color: ${props.theme.color.secondary200};
  `}
`

export const Close = styled(IconCross)`
  fill: ${(props: StyledProps<{}>) => props.theme.color.onPrimaryDark};
  position: absolute;
  top: 17px;
  left: 13px;
`

export const BurgerMenu = styled(IconBurgerMenu)`
  fill: ${(props: StyledProps<{}>) => props.theme.color.onPrimaryDark};
  cursor: pointer;
`

export const CountrySelectWrapper = styled.div`
  width: auto;
  margin-left: auto;
  margin-right: 16px;
  align-self: center;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  position: relative;

  ${props => props.theme.media.mobile`
    margin-right: 0px;`}
`

export const CountryOptionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

export const OptionTextWrapper = styled.span`
  margin-left: 8px;
`

export const CountrySelectControl = styled(selectComponents.Control)`
  border: none !important;
`

export const CountrySelectValue = styled(selectComponents.SingleValue)`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const CountrySuggestionWrapper = styled.div<{ show: boolean }>`
  z-index: 10;
  position: absolute;
  width: 400px;
  margin-top: 16px;
  right: 0;
  background: ${props => props.theme.color.onPrimaryLight};
  padding: 20px;
  box-shadow: 0px 4px 12px rgb(0 0 0 / 20%);
  margin-bottom: 25px;
  max-width: 400px;
  display: ${({ show }) => (show ? 'block' : 'none')};
  border-radius: 8px;

  ${props => props.theme.media.mobile`
    position: fixed;
  `}
`

const Text = styled.div`
  color: ${props => props.theme.color.textSecondary};
  font-size: 14px;
  margin-bottom: 15px;
`

const CountrySuggestButtonsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 15px;
`

export const CountrySuggestionPopoverBody: React.FC<{
  show: boolean
  cancelText: string
  acceptText: string
  onCancel: () => void
  onAccept: () => void
}> = ({ acceptText = 'Yes', cancelText = 'No', onCancel, onAccept, show }) => {
  const { t } = useTranslation(['common', 'header'])
  return (
    <CountrySuggestionWrapper show={show} data-test-id={'country-suggest-popup'}>
      <Text>{t('header:countrySuggest')}</Text>
      <CountrySuggestButtonsWrapper>
        <Button intent={'cancel'} onClick={onCancel} size={'small'} data-test-id={'decline-country-suggestion'}>
          {cancelText}
        </Button>
        <Button intent={'action'} onClick={onAccept} size={'small'} data-test-id={'accept-country-suggestion'}>
          {acceptText}
        </Button>
      </CountrySuggestButtonsWrapper>
    </CountrySuggestionWrapper>
  )
}
